import { useEffect, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import PinConfirm from "../components/PinConfirm";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

export default function VoucherDelete() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const { selected, router } = useRouters();
  const [mode, setMode] = useState("all");
  const [profiles, setProfiles] = useState([]);
  const [profile, setProfile] = useState("");
  const [vouchers, setVouchers] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  useEffect(() => {
    if (!selected) return;
    api.get(`/routers/${selected}/profiles`).then((r) => setProfiles(r.data.hotspot || [])).catch(() => {});
    api.get(`/routers/${selected}/vouchers`).then((r) => setVouchers(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [selected]);

  const names = Object.keys(checked).filter((k) => checked[k]);
  const label = mode === "all" ? "SEMUA voucher" : mode === "profile" ? `voucher profile ${profile}` : `${names.length} voucher terpilih`;

  const doExecute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/routers/${selected}/vouchers/delete-bulk`, { mode, profile: profile || undefined, names });
      toast.success(`${data.removed} voucher dihapus dari router`);
      setChecked({});
      const r = await api.get(`/routers/${selected}/vouchers`);
      setVouchers(r.data);
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };

  const execute = () => {
    if (isAdmin) { setPinOpen(true); return; }
    if (window.confirm(`Yakin hapus ${label} dari router ${router?.name}? Tindakan ini tidak bisa dibatalkan.`)) doExecute();
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="voucher-delete-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Hapus Voucher</h1>
        <p className="text-sm text-muted-foreground mt-1">Hapus voucher hotspot massal dari {router?.name || "router"}</p>
      </div>

      <Card className="bg-amber-500/5 border-amber-500/20">
        <CardContent className="p-4 flex gap-3 items-start">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200/80">Penghapusan langsung dieksekusi ke router MikroTik dan tidak dapat dibatalkan. Voucher bernama "admin" dan "default-trial" otomatis dilewati.</p>
        </CardContent>
      </Card>

      <Card className="bg-card/60 border-border">
        <CardHeader><CardTitle className="text-base font-heading">Mode Penghapusan</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5 max-w-xs">
            <Label>Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="select-delete-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Voucher</SelectItem>
                <SelectItem value="profile">Berdasarkan Profile</SelectItem>
                <SelectItem value="selected">Pilih Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "profile" && (
            <div className="space-y-1.5 max-w-xs">
              <Label>Profile</Label>
              <Select value={profile} onValueChange={setProfile}>
                <SelectTrigger data-testid="select-delete-profile"><SelectValue placeholder="Pilih profile" /></SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {mode === "selected" && (
            <div className="border border-border rounded-xl max-h-72 overflow-y-auto divide-y divide-border" data-testid="delete-voucher-list">
              {vouchers.map((v) => (
                <label key={v.name} className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/40 cursor-pointer">
                  <Checkbox checked={!!checked[v.name]} onCheckedChange={(c) => setChecked({ ...checked, [v.name]: !!c })} data-testid={`delete-checkbox-${v.name}`} />
                  <span className="font-mono text-sm">{v.name}</span>
                  <span className="text-xs text-muted-foreground font-mono ml-auto">{v.profile}</span>
                </label>
              ))}
              {!vouchers.length && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Tidak ada voucher / router offline</div>}
            </div>
          )}
          <Button variant="destructive" onClick={execute}
            disabled={loading || (mode === "profile" && !profile) || (mode === "selected" && !names.length)}
            data-testid="btn-execute-delete">
            <Trash2 className="w-4 h-4 mr-1.5" /> {loading ? "Menghapus..." : "Hapus Sekarang"}
          </Button>
        </CardContent>
      </Card>

      <PinConfirm
        open={pinOpen}
        onOpenChange={setPinOpen}
        title="Konfirmasi Hapus Massal"
        description={`Anda akan menghapus ${label} dari router ${router?.name || ""}. Tindakan ini tidak bisa dibatalkan.`}
        confirmLabel="Ya, Hapus Sekarang"
        destructive
        onConfirm={doExecute}
        testid="pin-delete-bulk-voucher"
      />
    </div>
  );
}
